import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  Shift,
  TimelineShift,
  TimelineShowActivitiesQueryParamKey,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import { SELECTED_REGION, TabEnum } from '@wilson/shift-timeline/services';
import {
  AddShiftWithoutActivitiesToCellViewUnassignedRegion,
  AddShiftWithoutActivitiesToUnassignedRegion,
  BulkAddShiftActivitiesToUnassignedRegion,
  DeleteShiftDeterminedActivities,
  RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion,
  RemoveShiftWithoutActivitiesFromUnassignedRegion,
  SetAreActivitiesVisible,
  ShiftTimelineDataState,
  ShiftTimelineDataTransformerService,
  ShiftTimelineSettingsState,
  TimelineCreateNewShiftV2,
  TimelineRemoveShiftFromUserV2,
  TimelineUnassignedShiftValidationsStateService,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';
import { TimelineCommonOperationsService } from './timeline-common-operations.service';
@Injectable({
  providedIn: 'root',
})
export class UnassignShiftCommonOperationsService {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly shiftsService = inject(ShiftsService);
  private readonly translate = inject(TranslateService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly timelineUnassignedShiftValidationsStateService = inject(
    TimelineUnassignedShiftValidationsStateService,
  );
  protected shiftTimelineDataTransformerService = inject(
    ShiftTimelineDataTransformerService,
  );

  public async handleCellViewUnassign(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      shifts.forEach((selectedShift) => {
        const convertedShift =
          this.shiftTimelineDataTransformerService.convertToUnassignedShift(
            selectedShift as Shift & WithPreparedAttributes & { id: string },
          );
        this.store.dispatch([
          new AddShiftWithoutActivitiesToCellViewUnassignedRegion(
            convertedShift,
          ),
          new TimelineRemoveShiftFromUserV2({
            userId: selectedShift.userId as string,
            shiftId: selectedShift.id as string,
          }),
        ]);
      });

      await this.processUnassignedShifts(shifts);
    } catch {
      this.undoCellViewUnassign(shifts);
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  public undoCellViewUnassign(shifts: (Shift & { id: string })[]): void {
    shifts.forEach((selectedShift) => {
      this.store.dispatch(
        new RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion({
          shiftId: selectedShift.id,
        }),
      );
      this.store.dispatch(
        new TimelineCreateNewShiftV2(
          {
            ...selectedShift,
            isHighlighted: true,
          } as TimelineShift,
          selectedShift.userId as string,
        ),
      );
    });
    this.timelineCommonOperationsService.updateUsersAssignmentsState(
      shifts.map((shift) => shift.userId as string),
    );
  }
  public async handleStandardViewUnassign(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      const areActivitiesVisible = this.store.selectSnapshot(
        ShiftTimelineSettingsState.areActivitiesVisible,
      );
      shifts.forEach((selectedShift) => {
        if (areActivitiesVisible) {
          const shiftActivities = this.store.selectSnapshot(
            ShiftTimelineDataState.shiftDeterminedActivities(
              selectedShift.id as string,
            ),
          );
          if (shiftActivities.length > 0) {
            this.store.dispatch([
              new DeleteShiftDeterminedActivities([selectedShift.id]),
              new BulkAddShiftActivitiesToUnassignedRegion(
                selectedShift.id,
                shiftActivities,
              ),
            ]);
          }
        }

        const convertedShift =
          this.shiftTimelineDataTransformerService.convertToUnassignedShift(
            selectedShift as Shift & WithPreparedAttributes & { id: string },
          );
        this.store.dispatch(
          new AddShiftWithoutActivitiesToUnassignedRegion(convertedShift),
        );
        this.store.dispatch(
          new TimelineRemoveShiftFromUserV2({
            userId: selectedShift.userId as string,
            shiftId: selectedShift.id as string,
          }),
        );
      });

      await this.processUnassignedShifts(shifts);
    } catch {
      this.undoStandardViewUnassign(shifts);
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  public undoStandardViewUnassign(shifts: (Shift & { id: string })[]): void {
    shifts.forEach((selectedShift) => {
      this.store.dispatch(
        new RemoveShiftWithoutActivitiesFromUnassignedRegion({
          shiftId: selectedShift.id,
        }),
      );

      this.store.dispatch(
        new TimelineCreateNewShiftV2(
          {
            ...selectedShift,
            isHighlighted: true,
          } as TimelineShift,
          selectedShift.userId as string,
        ),
      );
    });
    this.timelineCommonOperationsService.updateUsersAssignmentsState(
      shifts.map((shift) => shift.userId as string),
    );
  }

  changeTabSelection(userSelectedTab: TabEnum): void {
    let areActivitiesVisible = this.store.selectSnapshot(
      ShiftTimelineSettingsState.areActivitiesVisible,
    );
    if (userSelectedTab === TabEnum.UnassignedActivities) {
      areActivitiesVisible = true;
      this.store.dispatch(new SetAreActivitiesVisible(areActivitiesVisible));
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        [SELECTED_REGION]: userSelectedTab,
        [TimelineShowActivitiesQueryParamKey]: areActivitiesVisible,
      },
      queryParamsHandling: 'merge',
    });
  }

  private async processUnassignedShifts(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    const shiftIds = shifts.map((shift) => shift.id as string);

    await firstValueFrom(this.shiftsService.withdrawShifts(shiftIds));
    const userIds = shifts.map(
      (selectedShift) => selectedShift.userId as string,
    );
    this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers(
      userIds,
    );
    this.timelineCommonOperationsService.updateAssignedShiftsKPIs();
    this.timelineUnassignedShiftValidationsStateService.queueValidationRequest(
      shiftIds,
      {
        forceRefresh: true,
      },
    );
    this.nzMessageService.success(
      this.translate.instant('shift.page.success_multiple_unassign'),
    );
  }

  isCursorOutsideDropZone(event: DragEvent): boolean {
    event.preventDefault();

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const xMousePosition = event.clientX;
    const yMousePosition = event.clientY;

    if (
      xMousePosition <= rect.left ||
      xMousePosition >= rect.right ||
      yMousePosition <= rect.top ||
      yMousePosition >= rect.bottom
    ) {
      return true;
    } else {
      return false;
    }
  }
}
